import { LENS_PERIOD_FILTER_ID, type LensFilter, type LensFilterValues } from '~/types/leLens';

import type { LensRange } from './cardView';

export type ActiveFilterChip = {
  /** Filter id for a dashboard filter, or the column name for a dataset one. */
  id: string;
  label: string;
  value: string;
  kind: 'filter' | 'selection' | 'range';
};

/**
 * What is narrowing the board right now, as one chip each.
 *
 * A filter at rest is a control saying "All", which is the state nine boards in
 * ten are in: seven of those said nothing and cost a row and a half of every
 * page. What is worth permanent space is what is actually applied, so only that
 * is shown, and shown where it can be undone.
 *
 * The period is deliberately not here. There is always one, so it is never news
 * and it has its own place on the line.
 */
export function activeFilterChips(
  filters: ReadonlyArray<LensFilter>,
  values: LensFilterValues,
  selections: Record<string, Array<string>>,
  ranges: Record<string, LensRange>,
  labelFor: (column: string) => string,
): Array<ActiveFilterChip> {
  const chips: Array<ActiveFilterChip> = [];

  for (const filter of filters) {
    if (filter.id === LENS_PERIOD_FILTER_ID || filter.type === 'dateRange') {
      continue;
    }
    const value = values[filter.id];
    const text = Array.isArray(value) ? value.join(', ') : typeof value === 'string' ? value : '';
    if (text) {
      chips.push({ id: filter.id, kind: 'filter', label: filter.label, value: text });
    }
  }

  for (const [column, chosen] of Object.entries(selections)) {
    // A present column narrows even with nothing ticked, which is how `hasSelection` in cardView
    // reads it, so the empty case needs a chip too. Without one the cards go blank and the strip
    // that exists to explain and undo a narrowing says nothing at all.
    chips.push({
      id: column,
      kind: 'selection',
      label: labelFor(column),
      value: chosen.length ? chosen.join(', ') : 'nothing selected',
    });
  }

  for (const [column, range] of Object.entries(ranges)) {
    const text = describeRange(range);
    if (text) {
      chips.push({ id: column, kind: 'range', label: labelFor(column), value: text });
    }
  }

  return chips;
}

/** A bound at either end, or both, in the words the reader set them in. */
function describeRange(range: LensRange): string {
  const from = Number.isFinite(range.min) ? String(range.min) : '';
  const to = Number.isFinite(range.max) ? String(range.max) : '';
  if (from && to) return `${from} to ${to}`;
  if (from) return `from ${from}`;
  if (to) return `up to ${to}`;
  return '';
}
